import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { hasLink } from '@ngxp/rest';
import { KontaktKategorieLabel } from '@schir-int-client/adressverwaltung-shared';
import {
	AufgabeFacade,
	AufgabeLinkRel,
	AufgabeMessages,
	AufgabenTyp,
	AufgabeResource,
	AufgabeStatusLabel,
	AufgabeTypStaticTaetigkeitLabel,
} from '@schir-int-client/aufgabe-shared';
import { createEmptyStateResource, StateResource } from '@schir-int-client/ngrx-helpers';
import { DialogService } from '@schir-int-client/tech';
import { VerfahrenFacade } from '@schir-int-client/verfahren-shared';
import {
	KorrespondenzVorlageFacade,
	KorrespondenzVorlageLinkRel,
	KorrespondenzVorlageResource,
} from '@schir-int-client/korrespondenz-shared';
import { isNil, isUndefined } from 'lodash-es';
import { Observable, of, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import { VorlageFacade, VorlageResource } from '@schir-int-client/vorlage-shared';
import { VorlageService } from '../../../../../vorlage-shared/src/lib/vorlage.service';
import { RegisterName } from '@schir-int-client/register-shared';
import { getId, getIdNumber } from '../../../../../test-helpers/src/lib/resource-helper';
import { ChangeFileSuccessDialogComponent } from './change-file-success-dialog/change-file-success-dialog.component';

@Component({
	selector: 'schir-int-client-aufgabe-in-verfuegung',
	templateUrl: './aufgabe-in-verfuegung.component.html',
	styleUrls: ['./aufgabe-in-verfuegung.component.scss'],
	standalone: false,
})
export class AufgabeInVerfuegungComponent implements OnInit, OnChanges, OnDestroy {
	@Input() aufgabe: AufgabeResource;

	@Output() openKorrespondenz: EventEmitter<void> = new EventEmitter();
	@Output() openAufgabeDialog: EventEmitter<boolean> = new EventEmitter();

	public isSelected: Observable<boolean>;
	public aufgabeStatusLabel = AufgabeStatusLabel;
	public linkRel = AufgabeLinkRel;

	public verfahrenId: Observable<string>;
	public vorlage: Observable<VorlageResource>;
	public audioAlert: Subject<string> = new Subject<string>();

	public router = inject(Router);
	private facade = inject(AufgabeFacade);
	private verfahrenFacade = inject(VerfahrenFacade);
	private dialogService = inject(DialogService);
	private korrespondenzVorlageFacade = inject(KorrespondenzVorlageFacade);
	private vorlageFacade = inject(VorlageFacade);

	protected register: RegisterName;
	private subscriptions: Subscription[] = [];

	readonly aufgabeTypStaticTaetigkeitLabel = AufgabeTypStaticTaetigkeitLabel;
	readonly kontaktKategorieLabel = KontaktKategorieLabel;
	readonly aufgabenTyp = AufgabenTyp;
	readonly staticTaetigkeitLabel = AufgabeTypStaticTaetigkeitLabel;

	constructor() {
		this.verfahrenId = this.verfahrenFacade.verfahrenSingle.pipe(
			map(selectedVerfahren => {
				return isNil(selectedVerfahren) ? null : getId(selectedVerfahren);
			})
		);
	}

	ngOnInit() {
		this.isSelected = this.facade.isAufgabeSelected(this.aufgabe);
		this.subscriptions.push(
			this.isSelected.pipe(first()).subscribe(selected => {
				if (selected) {
					this.subscriptions.push(
						this.verfahrenId.pipe(first()).subscribe(verfahrenId => {
							this.setAufgabe();
							this.router.navigate([
								'/verfahren',
								verfahrenId,
								{
									outlets: {
										aufgabe: this.aufgabeId,
									},
								},
							]);
						})
					);
				}
			})
		);
		this.verfahrenFacade.verfahrenSingle.pipe(first()).subscribe(v => (this.register = v.register));
	}

	ngOnChanges() {
		if (!isUndefined(this.aufgabe) && hasLink(this.aufgabe, AufgabeLinkRel.KORRESPONDENZ_VORLAGE)) {
			this.vorlage = this.korrespondenzVorlageFacade.getKorrespondenzVorlageByAufgabe(this.aufgabe).pipe(
				filter(korrespondenzVorlageResource => korrespondenzVorlageResource.loaded),
				switchMap(korrespondenzVorlageResource =>
					getIdNumber(korrespondenzVorlageResource.resource, KorrespondenzVorlageLinkRel.VORLAGE) ===
					VorlageService.BENUTZERDEFINIERTE_VORLAGE_ID
						? of(<VorlageResource>{ name: 'Benutzerdefiniert' })
						: this.vorlageFacade.getVorlage(korrespondenzVorlageResource.resource)
				)
			);
		}
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	get aufgabeId() {
		return getId(this.aufgabe);
	}

	get korrespondenzVorlage(): Observable<StateResource<KorrespondenzVorlageResource>> {
		if (!isUndefined(this.aufgabe) && hasLink(this.aufgabe, AufgabeLinkRel.KORRESPONDENZ_VORLAGE)) {
			return this.korrespondenzVorlageFacade.getKorrespondenzVorlageByAufgabe(this.aufgabe);
		}
		return of(createEmptyStateResource<KorrespondenzVorlageResource>());
	}

	get hasZertifikatBody(): boolean {
		return (
			this.hasBodyWithElement('sendenAn', this.aufgabe) ||
			((this.isAufgabeWithTyp(AufgabenTyp.SCHIFFSBRIEF, this.aufgabe) ||
				this.isAufgabeWithTyp(AufgabenTyp.SCHIFFSZERTIFIKAT, this.aufgabe)) &&
				this.hasBodyWithElement('taetigkeit', this.aufgabe))
		);
	}

	get hasZertifikatsauszugBody(): boolean {
		return this.hasBodyWithElement('zertifikatTaetigkeit', this.aufgabe);
	}

	get hasVersandBody(): boolean {
		return this.hasBodyWithElement('empfaenger', this.aufgabe);
	}

	get isRegisterabschrift(): boolean {
		return this.isAufgabeWithTyp(AufgabenTyp.REGISTERABSCHRIFT, this.aufgabe);
	}

	get isEintragungsMitteilung(): boolean {
		return this.isAufgabeWithTyp(AufgabenTyp.EINTRAGUNGSMITTEILUNG, this.aufgabe);
	}

	get isWiedervorlage(): boolean {
		return this.isAufgabeWithTyp(AufgabenTyp.WIEDERVORLAGE, this.aufgabe);
	}

	get isRechnungsstellung(): boolean {
		return this.isAufgabeWithTyp(AufgabenTyp.RECHNUNGSSTELLUNG, this.aufgabe);
	}

	markAsFreigegeben(): void {
		this.facade.markAsFreigegeben(this.aufgabe);
		this.audioAlert.next('Die Aufgabe wurde als freigegeben markiert.');
	}

	markAsErledigt(): void {
		this.facade.markAsErledigt(this.aufgabe);
		this.audioAlert.next('Die Aufgabe wurde als erledigt markiert.');
	}

	setAufgabe(): void {
		this.facade.setAufgabe(this.aufgabe);
	}

	openKorrespondenzInAufgaben() {
		this.openKorrespondenz.emit();
	}

	deleteKorrespondenzVorlage(korrespondenzVorlage: KorrespondenzVorlageResource) {
		this.korrespondenzVorlageFacade.deleteKorrespondenzVorlage(korrespondenzVorlage);
	}

	uploadKorrespondenzVorlage(file: File, korrespondenzVorlage: KorrespondenzVorlageResource) {
		this.facade.setAufgabe(this.aufgabe);
		this.korrespondenzVorlageFacade.uploadKorrespondenzVorlage(korrespondenzVorlage, file);
		this.korrespondenzVorlageFacade.changeFileSuccess$.pipe(take(1), takeUntil(timer(20_000))).subscribe(action => {
			this.dialogService.openDialog(ChangeFileSuccessDialogComponent, {
				data: action,
			});
		});
	}

	delete() {
		this.subscriptions.push(
			this.dialogService
				.openDialogYesNo(AufgabeMessages.DELETE_AUFGABE)
				.pipe(take(1))
				.subscribe(data => {
					if (data.agree) {
						this.facade.deleteAufgabe(this.aufgabe);
					}
				})
		);
	}

	editAufgabe() {
		this.facade.setAufgabe(this.aufgabe);
		//Bei bestehenden Aufgaben mit Eingetragenem Eigentuemer muss dieser nach wie vor aus/abgewählt werden können
		const excludeEingetragenerEigentuemer = JSON.stringify(this.aufgabe.body).includes('EINGETRAGENER_EIGENTUEMER');
		this.openAufgabeDialog.emit(!excludeEingetragenerEigentuemer);
	}

	private hasBodyWithElement(element: string, aufgabe: AufgabeResource): boolean {
		return !isNil(aufgabe.body) && element in this.aufgabe.body;
	}

	private isAufgabeWithTyp(typ: AufgabenTyp, aufgabe: AufgabeResource) {
		return !isNil(aufgabe.body) && aufgabe.typ === typ;
	}
}
